import React, { useMemo, useState } from 'react';
import { History, Droplets, Hand, CalendarClock, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntlFormatting } from '../utils/intl';
import { useIrrigation } from '../contexts/IrrigationContext';

const IrrigationHistory: React.FC = () => {
  const { t } = useTranslation('irrigation');
  const { formatNumber, formatDateTime } = useIntlFormatting();
  const { history, zones } = useIrrigation();
  const [zoneFilter, setZoneFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');

  const filtered = useMemo(
    () =>
      history.filter((h) => {
        const zoneMatch = zoneFilter === 'all' || h.zone_id === zoneFilter;
        const kindMatch = kindFilter === 'all' || h.kind === kindFilter;
        return zoneMatch && kindMatch;
      }),
    [history, zoneFilter, kindFilter]
  );

  const totalWater = filtered.reduce((s, h) => s + h.water_liters, 0);
  const totalMinutes = filtered.reduce((s, h) => s + h.duration_minutes, 0);
  const manualCount = filtered.filter((h) => h.kind === 'manual').length;

  const stats = [
    { label: t('stats.waterings'), value: formatNumber(filtered.length), color: 'var(--color-primary)', icon: History },
    { label: t('stats.totalWater'), value: `${formatNumber(totalWater)} L`, color: 'var(--color-info)', icon: Droplets },
    { label: t('stats.totalDuration'), value: `${formatNumber(Math.round(totalMinutes / 60))} h`, color: 'var(--color-success)', icon: CalendarClock },
    { label: t('stats.manualStarts'), value: formatNumber(manualCount), color: 'var(--color-accent)', icon: Hand },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-heading-2 text-[var(--text-primary)]">{t('title')}</h1>
        <p className="text-body text-[var(--text-secondary)] mt-1">
          {t('subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="card text-center" data-testid={`history-stat-${i}`}>
            <s.icon className="w-6 h-6 mx-auto mb-2" style={{ color: s.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: s.color }}>
              {s.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-[var(--text-secondary)]">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">{t('filters.label')}</span>
        </div>
        <select
          value={zoneFilter}
          onChange={(e) => setZoneFilter(e.target.value)}
          className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
          data-testid="history-zone-filter"
        >
          <option value="all">{t('filters.allZones')}</option>
          {zones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.name}
            </option>
          ))}
        </select>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
          data-testid="history-kind-filter"
        >
          <option value="all">{t('filters.allTypes')}</option>
          <option value="scheduled">{t('kind.scheduled')}</option>
          <option value="manual">{t('kind.manual')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="history-table">
          <thead>
            <tr className="border-b border-[var(--border-light)] text-left text-[var(--text-secondary)]">
              <th className="px-4 py-3 font-medium">{t('table.zone')}</th>
              <th className="px-4 py-3 font-medium">{t('table.type')}</th>
              <th className="px-4 py-3 font-medium">{t('table.origin')}</th>
              <th className="px-4 py-3 font-medium">{t('table.start')}</th>
              <th className="px-4 py-3 font-medium">{t('table.end')}</th>
              <th className="px-4 py-3 font-medium">{t('table.duration')}</th>
              <th className="px-4 py-3 font-medium">{t('table.water')}</th>
              <th className="px-4 py-3 font-medium">{t('table.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((h) => (
              <tr
                key={h.id}
                className="border-b border-[var(--border-light)] last:border-0 hover:bg-[var(--gray-50)]"
                data-testid={`history-row-${h.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-[var(--text-primary)]">{h.zone_name}</div>
                  <div className="text-xs text-[var(--text-tertiary)]">{h.field_name}</div>
                </td>
                <td className="px-4 py-3 capitalize text-[var(--text-secondary)]">{h.type}</td>
                <td className="px-4 py-3">
                  {h.kind === 'manual' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
                      <Hand className="w-3 h-3" /> {t('kind.manual')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-md bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
                      <CalendarClock className="w-3 h-3" /> {h.program_name || t('kind.scheduled')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{formatDateTime(h.started_at)}</td>
                <td className="px-4 py-3 text-[var(--text-secondary)]">{formatDateTime(h.ended_at)}</td>
                <td className="px-4 py-3 text-[var(--text-primary)] font-medium">{formatNumber(h.duration_minutes)} min</td>
                <td className="px-4 py-3 text-[var(--color-info)] font-medium">{formatNumber(h.water_liters)} L</td>
                <td className="px-4 py-3">
                  {h.status === 'completed' ? (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-success)]/15 text-[var(--color-success)]">
                      {t('status.completed')}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--color-warning)]/15 text-[var(--color-warning)]">
                      {t('status.stopped')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-12" data-testid="history-empty">
            <History className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4" />
            <p className="text-body text-[var(--text-secondary)]">{t('empty')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default IrrigationHistory;
